import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { getPosts, deletePost } from '../../api/admin/client';

export default function PostsPage() {
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, [pagination.page, filter]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const data = await getPosts({
        page: pagination.page,
        limit: 20,
        status: filter !== 'all' ? filter : undefined,
        search: search || undefined
      });
      setPosts(data.posts);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchPosts();
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }

    try {
      await deletePost(id);
      fetchPosts();
    } catch (err) {
      alert('Error deleting post: ' + err.message);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      published: 'admin-badge-success',
      draft: 'admin-badge-warning',
      archived: 'admin-badge-info'
    };
    return badges[status] || 'admin-badge-info';
  };

  return (
    <AdminLayout title="Posts">
      {/* Header Actions */}
      <div className="admin-card">
        <div className="admin-card-body">
          <div className="admin-flex admin-justify-between admin-items-center" style={{ flexWrap: 'wrap', gap: '16px' }}>
            <div className="admin-flex admin-gap-2">
              <select
                className="admin-select"
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                style={{ width: 'auto' }}
              >
                <option value="all">All Posts</option>
                <option value="published">Published</option>
                <option value="draft">Drafts</option>
                <option value="archived">Archived</option>
              </select>

              <form onSubmit={handleSearch} className="admin-flex admin-gap-2">
                <input
                  type="text"
                  className="admin-input"
                  placeholder="Search posts..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: '200px' }}
                />
                <button type="submit" className="admin-btn admin-btn-secondary">
                  Search
                </button>
              </form>
            </div>

            <Link to="/admin/posts/new" className="admin-btn admin-btn-primary">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Post
            </Link>
          </div>
        </div>
      </div>

      {/* Posts Table */}
      <div className="admin-card">
        <div className="admin-card-body" style={{ padding: 0 }}>
          {loading ? (
            <div className="admin-loading">
              <div className="admin-spinner"></div>
            </div>
          ) : posts.length === 0 ? (
            <div className="admin-empty">
              <div className="admin-empty-icon">📝</div>
              <div className="admin-empty-title">No posts found</div>
              <p>Get started by creating your first post.</p>
              <Link to="/admin/posts/new" className="admin-btn admin-btn-primary" style={{ marginTop: '16px' }}>
                Create Post
              </Link>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Author</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th style={{ width: '120px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map(post => (
                  <tr key={post.id}>
                    <td>
                      <Link
                        to={`/admin/posts/${post.id}`}
                        style={{ color: 'var(--admin-primary)', textDecoration: 'none', fontWeight: 500 }}
                      >
                        {post.title.length > 60 ? post.title.substring(0, 60) + '...' : post.title}
                      </Link>
                      {post.isFeatured && (
                        <span className="admin-badge admin-badge-info" style={{ marginLeft: '8px' }}>
                          Featured
                        </span>
                      )}
                    </td>
                    <td>{post.authorName || '-'}</td>
                    <td>{post.categoryName || '-'}</td>
                    <td>
                      <span className={`admin-badge ${getStatusBadge(post.status)}`}>
                        {post.status}
                      </span>
                    </td>
                    <td className="admin-text-muted">
                      {new Date(post.createdAt).toLocaleDateString()}
                    </td>
                    <td>
                      <div className="admin-flex admin-gap-2">
                        <button
                          onClick={() => navigate(`/admin/posts/${post.id}`)}
                          className="admin-btn admin-btn-sm admin-btn-secondary"
                          title="Edit"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(post.id, post.title)}
                          className="admin-btn admin-btn-sm admin-btn-danger"
                          title="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="admin-pagination">
            <button
              className="admin-pagination-btn"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
            >
              Previous
            </button>

            {[...Array(pagination.totalPages)].map((_, i) => (
              <button
                key={i + 1}
                className={`admin-pagination-btn ${pagination.page === i + 1 ? 'active' : ''}`}
                onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}
              >
                {i + 1}
              </button>
            )).slice(
              Math.max(0, pagination.page - 3),
              Math.min(pagination.totalPages, pagination.page + 2)
            )}

            <button
              className="admin-pagination-btn"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
